/**
 * Regras puras do módulo pré-natal (sem Vue, sem HTTP), testáveis com node --test.
 *
 * A datação espelha o GestationalAgeCalculator da API: o resultado local é só
 * prévia enquanto o profissional digita; quem grava é sempre o servidor.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const EARLY_ULTRASOUND_MAX_DAYS = 97;
export const ULTRASOUND_MAX_DAYS = 140;

/**
 * @param {string|null|undefined} value  'YYYY-MM-DD' (ou ISO com hora)
 * @returns {Date|null} meia-noite UTC do dia
 */
export function parseDate(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {Date} date
 * @returns {string} 'YYYY-MM-DD'
 */
export function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

export function todayIso(now = new Date()) {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, 10);
}

export function addDays(isoDate, days) {
  const date = parseDate(isoDate);

  return date ? toIsoDate(new Date(date.getTime() + days * DAY_MS)) : null;
}

export function diffDays(fromIso, toIso) {
  const from = parseDate(fromIso);
  const to = parseDate(toIso);

  return from && to ? Math.round((to.getTime() - from.getTime()) / DAY_MS) : null;
}

/**
 * Soma meses sem estourar o fim do mês (31/05 + 9 meses = 28 ou 29/02).
 */
export function addMonthsNoOverflow(isoDate, months) {
  const date = parseDate(isoDate);
  if (!date) {
    return null;
  }

  const targetMonthIndex = date.getUTCMonth() + months;
  const year = date.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
  const month = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  return toIsoDate(new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay))));
}

/**
 * Regra de Nägele: DUM + 7 dias + 9 meses.
 */
export function naegeleDueDate(lmpIso) {
  const plusSeven = addDays(lmpIso, 7);

  return plusSeven ? addMonthsNoOverflow(plusSeven, 9) : null;
}

export function trimester(gestationalAgeDays) {
  if (gestationalAgeDays < 98) {
    return 1;
  }

  return gestationalAgeDays < 196 ? 2 : 3;
}

/**
 * @returns {string} '20s3d'
 */
export function formatGestationalAge(gestationalAgeDays) {
  if (gestationalAgeDays === null || gestationalAgeDays === undefined || Number.isNaN(gestationalAgeDays)) {
    return '—';
  }

  return `${Math.floor(gestationalAgeDays / 7)}s${gestationalAgeDays % 7}d`;
}

/**
 * Limite de discrepância DUM × USG para redatar (ACOG Committee Opinion 700).
 */
export function redatingThresholdDays(ultrasoundGestationalAgeDays) {
  if (ultrasoundGestationalAgeDays < 63) return 5;
  if (ultrasoundGestationalAgeDays < 112) return 7;
  if (ultrasoundGestationalAgeDays < 154) return 10;
  if (ultrasoundGestationalAgeDays < 196) return 14;

  return 21;
}

function toInteger(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number.parseInt(value, 10);

  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Prévia da datação: mesma escolha DUM × USG precoce feita pela API.
 *
 * @param {{lmpDate?: string, lmpReliable?: boolean, usExamDate?: string, usGaWeeks?: number|string, usGaDays?: number|string}} input
 * @param {string} referenceDate  'YYYY-MM-DD'
 * @returns {null|{referenceLmpDate: string, eddDate: string, gestationalAgeDays: number, label: string, trimester: number, method: 'lmp'|'ultrasound', redatedByUltrasound: boolean, warnings: string[]}}
 */
export function estimateDating(input, referenceDate) {
  const lmp = parseDate(input?.lmpDate) ? input.lmpDate.slice(0, 10) : null;
  const usWeeks = toInteger(input?.usGaWeeks);
  const usExamDate = parseDate(input?.usExamDate) ? input.usExamDate.slice(0, 10) : null;
  const ultrasound = usExamDate && usWeeks !== null
    ? { examDate: usExamDate, gaDays: usWeeks * 7 + (toInteger(input?.usGaDays) ?? 0) }
    : null;

  if (!lmp && !ultrasound) {
    return null;
  }

  const build = (referenceLmpDate, method, redatedByUltrasound = false) => {
    const gestationalAgeDays = diffDays(referenceLmpDate, referenceDate);

    return {
      referenceLmpDate,
      eddDate: naegeleDueDate(referenceLmpDate),
      gestationalAgeDays,
      label: formatGestationalAge(gestationalAgeDays),
      trimester: trimester(gestationalAgeDays),
      method,
      redatedByUltrasound,
      warnings: [],
    };
  };

  let result;
  if (!ultrasound) {
    result = build(lmp, 'lmp');
  } else {
    const ultrasoundLmp = addDays(ultrasound.examDate, -ultrasound.gaDays);
    const lmpReliable = input?.lmpReliable !== false;

    if (!lmp || !lmpReliable) {
      result = build(ultrasoundLmp, 'ultrasound');
    } else {
      const discrepancy = Math.abs(diffDays(lmp, ultrasoundLmp));
      result = discrepancy > redatingThresholdDays(ultrasound.gaDays)
        ? build(ultrasoundLmp, 'ultrasound', true)
        : build(lmp, 'lmp');
    }

    if (ultrasound.gaDays > EARLY_ULTRASOUND_MAX_DAYS) {
      result.warnings.push('USG após 13s6d: a datação por ultrassom perde precisão.');
    }
  }

  if (result.gestationalAgeDays < 0) {
    result.warnings.push('A data de referência é anterior à DUM calculada.');
  } else if (result.gestationalAgeDays >= 308) {
    result.warnings.push('IG acima de 44 semanas: revise as datas.');
  }

  return result;
}

/**
 * @returns {number|null} IMC com 1 casa
 */
export function bodyMassIndex(weightKg, heightCm) {
  const weight = parseDecimal(weightKg);
  const height = parseDecimal(heightCm);

  if (!weight || !height) {
    return null;
  }

  return Math.round((weight / ((height / 100) ** 2)) * 10) / 10;
}

/**
 * Aceita vírgula decimal ('65,5').
 */
export function parseDecimal(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(String(value).replace(',', '.'));

  return Number.isNaN(parsed) ? null : parsed;
}

export function fetusLabel(fetusNumber) {
  return `Feto ${String.fromCharCode(64 + Number(fetusNumber))}`;
}

/**
 * Estado gravado no atributo `prenatal_panel` do prontuário.
 *
 * @returns {{id_prenatal_visit: number|null, id_pregnancy: number|null}}
 */
export function parsePanelState(text) {
  const empty = { id_prenatal_visit: null, id_pregnancy: null };

  if (!text) {
    return empty;
  }

  try {
    const parsed = typeof text === 'string' ? JSON.parse(text) : text;

    return {
      id_prenatal_visit: parsed?.id_prenatal_visit ? Number(parsed.id_prenatal_visit) : null,
      id_pregnancy: parsed?.id_pregnancy ? Number(parsed.id_pregnancy) : null,
    };
  } catch (_) {
    return empty;
  }
}

export function emptyVisitForm(visitDate, fetusCount = 1) {
  return {
    visit_date: visitDate,
    weight_kg: '',
    systolic: '',
    diastolic: '',
    fundal_height_cm: '',
    fetal_heart_rate: '',
    fetal_presentation: '',
    edema: '',
    fetal_movement: '',
    notes: '',
    fetuses: fetusRows(fetusCount, []),
  };
}

function fetusRows(fetusCount, existing) {
  if (fetusCount <= 1) {
    return [];
  }

  return Array.from({ length: fetusCount }, (_, index) => {
    const number = index + 1;
    const current = (existing ?? []).find((fetus) => Number(fetus.fetus_number) === number);

    return {
      fetus_number: number,
      fetal_heart_rate: current?.fetal_heart_rate ?? '',
      fetal_presentation: current?.fetal_presentation ?? '',
    };
  });
}

/**
 * Formulário a partir da consulta devolvida pela API.
 */
export function visitToForm(visit, fetusCount = 1) {
  const value = (field) => (visit?.[field] ?? '');

  return {
    visit_date: visit.visit_date,
    weight_kg: value('weight_kg'),
    systolic: value('systolic'),
    diastolic: value('diastolic'),
    fundal_height_cm: value('fundal_height_cm'),
    fetal_heart_rate: value('fetal_heart_rate'),
    fetal_presentation: value('fetal_presentation'),
    edema: value('edema'),
    fetal_movement: value('fetal_movement'),
    notes: value('notes'),
    fetuses: fetusRows(fetusCount, visit.fetuses),
  };
}

/**
 * Corpo do POST/PUT da consulta. Em gestação múltipla, BCF e apresentação vão
 * por feto (os campos únicos são enviados vazios).
 */
export function buildVisitPayload(form, fetusCount = 1) {
  const text = (value) => (value === '' || value === undefined ? null : value);
  const integer = (value) => toInteger(value);
  const multiple = fetusCount > 1;

  const payload = {
    visit_date: form.visit_date,
    weight_kg: parseDecimal(form.weight_kg),
    systolic: integer(form.systolic),
    diastolic: integer(form.diastolic),
    fundal_height_cm: parseDecimal(form.fundal_height_cm),
    fetal_heart_rate: multiple ? null : integer(form.fetal_heart_rate),
    fetal_presentation: multiple ? null : text(form.fetal_presentation),
    edema: text(form.edema),
    fetal_movement: text(form.fetal_movement),
    notes: text(form.notes),
  };

  if (multiple) {
    payload.fetuses = (form.fetuses ?? [])
      .map((fetus) => ({
        fetus_number: Number(fetus.fetus_number),
        fetal_heart_rate: integer(fetus.fetal_heart_rate),
        fetal_presentation: text(fetus.fetal_presentation),
      }))
      .filter((fetus) => fetus.fetal_heart_rate !== null || fetus.fetal_presentation !== null);
  }

  return payload;
}

/**
 * Campos vazios viram null; números aceitam vírgula.
 *
 * @param {object} form
 * @param {string[]} numericFields
 */
export function buildPayload(form, numericFields = []) {
  return Object.fromEntries(Object.entries(form).map(([field, value]) => {
    if (value === '' || value === undefined) {
      return [field, null];
    }

    return [field, numericFields.includes(field) ? parseDecimal(value) : value];
  }));
}

/**
 * Erros 422 do Laravel: { campo: 'primeira mensagem' }.
 */
export function validationErrors(error) {
  const errors = error?.response?.status === 422 ? error.response.data?.errors : null;

  if (!errors) {
    return {};
  }

  return Object.fromEntries(Object.entries(errors).map(([field, messages]) => [
    field,
    Array.isArray(messages) ? messages[0] : messages,
  ]));
}

export function patientBaseUrl(professionalId, patientId) {
  return `/professionals/${professionalId}/patients/${patientId}`;
}

export function pregnancyUrl(professionalId, patientId, pregnancyId, path = '') {
  return `${patientBaseUrl(professionalId, patientId)}/pregnancies/${pregnancyId}${path}`;
}

export function alertClass(severity) {
  return severity === 'critical' ? 'alert-danger' : 'alert-warning';
}

export function sortAlerts(alerts) {
  const weight = (alert) => (alert.severity === 'critical' ? 0 : 1);

  return [...(alerts ?? [])].sort((a, b) => weight(a) - weight(b));
}

export const PRESENTATION_OPTIONS = [
  { value: 'cephalic', label: 'Cefálica' },
  { value: 'breech', label: 'Pélvica' },
  { value: 'transverse', label: 'Transversa' },
  { value: 'undetermined', label: 'Não definida' },
];

export const EDEMA_OPTIONS = [
  { value: 'absent', label: 'Ausente' },
  { value: 'plus_1', label: '+/4+' },
  { value: 'plus_2', label: '++/4+' },
  { value: 'plus_3', label: '+++/4+' },
  { value: 'plus_4', label: '++++/4+' },
];

export const FETAL_MOVEMENT_OPTIONS = [
  { value: 'present', label: 'Presente' },
  { value: 'reduced', label: 'Diminuída' },
  { value: 'absent', label: 'Ausente' },
  { value: 'not_assessed', label: 'Não avaliada' },
];

export const RISK_OPTIONS = [
  { value: 'habitual', label: 'Risco habitual' },
  { value: 'high', label: 'Alto risco' },
];

export const CLOSE_STATUS_OPTIONS = [
  { value: 'delivered', label: 'Parto realizado' },
  { value: 'abortion', label: 'Abortamento' },
  { value: 'transferred', label: 'Transferida' },
];

export const OUTCOME_TYPE_OPTIONS = [
  { value: 'vaginal', label: 'Parto vaginal' },
  { value: 'cesarean', label: 'Parto cesáreo' },
  { value: 'abortion', label: 'Abortamento' },
  { value: 'stillbirth', label: 'Natimorto' },
];

export const EXAM_OPTIONS = [
  { value: 'hemogram', label: 'Hemograma' },
  { value: 'blood_type', label: 'Tipagem sanguínea ABO/Rh' },
  { value: 'indirect_coombs', label: 'Coombs indireto' },
  { value: 'fasting_glucose', label: 'Glicemia de jejum' },
  { value: 'ogtt_75g', label: 'TOTG 75 g' },
  { value: 'syphilis', label: 'Sífilis (teste rápido / VDRL)' },
  { value: 'hiv', label: 'HIV (teste rápido / sorologia)' },
  { value: 'hepatitis_b', label: 'Hepatite B (HBsAg)' },
  { value: 'hepatitis_c', label: 'Hepatite C (anti-HCV)' },
  { value: 'toxoplasmosis', label: 'Toxoplasmose (IgG/IgM)' },
  { value: 'urinalysis', label: 'Urina tipo 1 (EAS)' },
  { value: 'urine_culture', label: 'Urocultura' },
  { value: 'hemoglobin_electrophoresis', label: 'Eletroforese de hemoglobina' },
  { value: 'tsh', label: 'TSH' },
  { value: 'gbs_culture', label: 'Cultura para estreptococo do grupo B' },
  { value: 'cervical_cytology', label: 'Citopatológico do colo uterino' },
  { value: 'other', label: 'Outro' },
];

export const EXAM_INTERPRETATION_OPTIONS = [
  { value: 'pending', label: 'Aguardando resultado' },
  { value: 'normal', label: 'Normal' },
  { value: 'altered', label: 'Alterado' },
];

export const ULTRASOUND_TYPE_OPTIONS = [
  { value: 'first_trimester', label: 'Obstétrica de 1º trimestre' },
  { value: 'morphology_first', label: 'Morfológica de 1º trimestre' },
  { value: 'morphology_second', label: 'Morfológica de 2º trimestre' },
  { value: 'obstetric', label: 'Obstétrica' },
  { value: 'obstetric_doppler', label: 'Obstétrica com Doppler' },
  { value: 'cervical_length', label: 'Medida do colo uterino' },
];

export const PLACENTA_OPTIONS = [
  { value: 'anterior', label: 'Anterior' },
  { value: 'posterior', label: 'Posterior' },
  { value: 'fundal', label: 'Fúndica' },
  { value: 'lateral', label: 'Lateral' },
  { value: 'low_lying', label: 'Baixa' },
  { value: 'previa', label: 'Prévia' },
];

export const DOPPLER_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'altered', label: 'Alterado' },
  { value: 'not_performed', label: 'Não realizado' },
];

export const VACCINE_OPTIONS = [
  { value: 'dtpa', label: 'dTpa (tríplice bacteriana acelular)' },
  { value: 'dt', label: 'dT (dupla adulto)' },
  { value: 'hepatitis_b', label: 'Hepatite B' },
  { value: 'influenza', label: 'Influenza' },
  { value: 'covid_19', label: 'COVID-19' },
  { value: 'rsv', label: 'VSR (vírus sincicial respiratório)' },
];

export const VACCINATION_STATUS_OPTIONS = [
  { value: 'applied', label: 'Aplicada' },
  { value: 'immune', label: 'Imune / esquema completo prévio' },
  { value: 'refused', label: 'Recusada' },
  { value: 'contraindicated', label: 'Contraindicada' },
];

export const WEIGHT_GAIN_STANDARD_OPTIONS = [
  { value: 'ms_2022', label: 'Ministério da Saúde 2022' },
  { value: 'iom_2009', label: 'IOM 2009' },
];

const CHECKLIST_STATUS = {
  done: { label: 'Realizado', badge: 'badge-light-success' },
  pending: { label: 'Pendente', badge: 'badge-light-warning' },
  overdue: { label: 'Em atraso', badge: 'badge-light-danger' },
  upcoming: { label: 'Futuro', badge: 'badge-light' },
  not_applicable: { label: 'Não se aplica', badge: 'badge-light' },
  in_progress: { label: 'Em andamento', badge: 'badge-light-info' },
  conditional: { label: 'Se indicado', badge: 'badge-light' },
  immune: { label: 'Imune', badge: 'badge-light-success' },
  refused: { label: 'Recusada', badge: 'badge-light-dark' },
  contraindicated: { label: 'Contraindicada', badge: 'badge-light-dark' },
};

export function checklistStatus(status, kind = 'exam') {
  const entry = CHECKLIST_STATUS[status] ?? { label: status, badge: 'badge-light' };

  if (status === 'done' && kind === 'vaccine') {
    return { ...entry, label: 'Aplicada' };
  }

  return entry;
}

/**
 * Agrupa o checklist de exames pelo período do protocolo, mantendo a ordem.
 *
 * @returns {{period: string, items: object[]}[]}
 */
export function groupChecklistByPeriod(checklist) {
  const groups = [];

  for (const item of checklist ?? []) {
    let group = groups.find((entry) => entry.period === item.period);
    if (!group) {
      group = { period: item.period, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }

  return groups;
}

/**
 * Contagem por status, para os cartões de resumo.
 */
export function checklistCounts(checklist) {
  return (checklist ?? []).reduce((counts, item) => {
    counts[item.status] = (counts[item.status] ?? 0) + 1;

    return counts;
  }, {});
}

const CHART_TEXT = { fontSize: '11px', color: '#7e8299' };
const CLASSIFICATION_COLORS = { below: '#F1416C', adequate: '#50CD89', above: '#FFC700', no_reference: '#A1A5B7' };

function baseChartOptions(yTitle, xMin, xMax) {
  return {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
    dataLabels: { enabled: false },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '11px' },
    grid: { borderColor: '#eff2f5', strokeDashArray: 4 },
    xaxis: {
      type: 'numeric',
      min: xMin,
      max: xMax,
      tickAmount: Math.min(xMax - xMin, 14),
      title: { text: 'Idade gestacional (semanas)', style: CHART_TEXT },
      labels: { style: { fontSize: '11px', colors: '#7e8299' }, formatter: (value) => `${Math.round(value)}` },
    },
    yaxis: {
      title: { text: yTitle, style: CHART_TEXT },
      labels: {
        style: { fontSize: '11px', colors: '#7e8299' },
        formatter: (value) => (value === null || value === undefined ? '' : Number(value).toFixed(1)),
      },
    },
  };
}

function pointTooltip(points, describe) {
  return ({ series, seriesIndex, dataPointIndex }) => {
    if (seriesIndex !== series.length - 1) {
      return '';
    }

    const point = points[dataPointIndex];

    return point ? `<div class="px-3 py-2">${describe(point)}</div>` : '';
  };
}

/**
 * Curva de altura uterina: p10/p50/p90 da referência + medidas da gestante.
 */
export function buildFundalHeightChart(chart) {
  if (!chart) {
    return null;
  }

  const points = chart.points ?? [];
  const reference = ['p10', 'p50', 'p90'].map((key) => ({
    name: key.toUpperCase(),
    type: 'line',
    data: (chart.series?.[key] ?? []).map((point) => [point.x, point.y]),
  }));

  const options = baseChartOptions('Altura uterina (cm)', chart.x_axis?.min ?? 13, chart.x_axis?.max ?? 40);

  return {
    series: [...reference, { name: 'Gestante', type: 'scatter', data: points.map((point) => [point.x, point.y]) }],
    options: {
      ...options,
      colors: ['#D1D5E0', '#50CD89', '#D1D5E0', '#780f5a'],
      stroke: { curve: 'straight', width: [1.5, 2.5, 1.5, 0], dashArray: [4, 0, 4, 0] },
      markers: {
        size: [0, 0, 0, 6],
        strokeWidth: [0, 0, 0, 2],
        discrete: points.map((point, index) => ({
          seriesIndex: 3,
          dataPointIndex: index,
          fillColor: CLASSIFICATION_COLORS[point.classification] ?? '#780f5a',
          strokeColor: '#fff',
          size: 6,
        })),
      },
      tooltip: {
        shared: false,
        intersect: true,
        custom: pointTooltip(points, (point) => `
          <div class="fw-bold mb-1">${formatDateBr(point.visit_date)}</div>
          <div>IG: ${formatGestationalAge(point.gestational_age_days)}</div>
          <div>AU: ${point.y} cm</div>`),
      },
      subtitle: { text: chart.reference_standard ?? '', align: 'left', style: { fontSize: '10px', color: '#A1A5B7' } },
    },
  };
}

/**
 * Faixa de ganho de peso recomendada para o IMC pré-gestacional + ganho observado.
 */
export function buildWeightGainChart(chart) {
  if (!chart || chart.error) {
    return null;
  }

  const points = chart.points ?? [];
  const options = baseChartOptions('Ganho de peso (kg)', chart.x_axis?.min ?? 0, chart.x_axis?.max ?? 40);

  return {
    series: [
      { name: 'Mínimo recomendado', type: 'line', data: (chart.series?.lower ?? []).map((point) => [point.x, point.y]) },
      { name: 'Máximo recomendado', type: 'line', data: (chart.series?.upper ?? []).map((point) => [point.x, point.y]) },
      { name: 'Gestante', type: 'scatter', data: points.map((point) => [point.x, point.y]) },
    ],
    options: {
      ...options,
      colors: ['#50CD89', '#FFC700', '#780f5a'],
      stroke: { curve: 'straight', width: [2, 2, 0], dashArray: [4, 4, 0] },
      markers: { size: [0, 0, 6], strokeWidth: [0, 0, 2] },
      tooltip: {
        shared: false,
        intersect: true,
        custom: pointTooltip(points, (point) => `
          <div class="fw-bold mb-1">${formatDateBr(point.visit_date)}</div>
          <div>IG: ${formatGestationalAge(point.gestational_age_days)}</div>
          <div>Peso: ${point.weight_kg} kg</div>
          <div>Ganho: ${Number(point.y).toFixed(1)} kg</div>`),
      },
      subtitle: { text: chart.reference_standard ?? '', align: 'left', style: { fontSize: '10px', color: '#A1A5B7' } },
    },
  };
}

export function formatDateBr(isoDate) {
  const date = parseDate(isoDate);
  if (!date) {
    return '—';
  }

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');

  return `${day}/${month}/${date.getUTCFullYear()}`;
}

export function formatNumber(value, unit = '', decimals = 1) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const formatted = Number(value).toLocaleString('pt-BR', { maximumFractionDigits: decimals });

  return unit ? `${formatted} ${unit}` : formatted;
}

/**
 * BCF da consulta em texto: único ou por feto ("A 140 · B 150").
 */
export function fetalHeartRateText(visit) {
  if (visit?.fetuses?.length) {
    const parts = visit.fetuses
      .filter((fetus) => fetus.fetal_heart_rate !== null && fetus.fetal_heart_rate !== undefined)
      .map((fetus) => `${fetusLabel(fetus.fetus_number).replace('Feto ', '')} ${fetus.fetal_heart_rate}`);

    return parts.length ? parts.join(' · ') : '—';
  }

  return visit?.fetal_heart_rate ? `${visit.fetal_heart_rate}` : '—';
}
